import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, ArrowRight, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/faqs`);
        setFaqs(response.data);
      } catch (_) {
        // FAQs fetch failed
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="faq-page">

      {/* ── Orange Hero ──────────────────────────── */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="faq-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <HelpCircle className="h-4 w-4" /> Got Questions?
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-heading mb-5 tracking-tight drop-shadow-lg">
              Frequently Asked Questions
            </h1>
            <p className="text-base lg:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about our cleaning services.
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full"><path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/></svg>
        </div>
      </section>

      {/* ── FAQ Accordion ────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">Answers</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">Common Questions</h2>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F56F05] mx-auto" />
            </div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq, i) => {
                const isOpen = openIndex === i;
                return (
                  <motion.div key={faq.q} {...fadeUp} transition={{ delay: i * 0.04 }}
                    className={`rounded-2xl border-2 transition-all duration-200 ${isOpen ? 'border-[#F56F05] bg-[#FFF7ED]' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}>
                    <button onClick={() => setOpenIndex(isOpen ? -1 : i)}
                      className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-${i}`}>
                      <span className={`font-bold text-sm pr-4 ${isOpen ? 'text-[#F56F05]' : 'text-zinc-900'}`}>{faq.q}</span>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${isOpen ? 'bg-[#F56F05] text-white rotate-180' : 'bg-zinc-100 text-zinc-500'}`}>
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                          className="overflow-hidden">
                          <div className="px-5 pb-5">
                            <p className="text-sm text-zinc-600 leading-relaxed">{faq.a}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Still have questions? */}
          <motion.div {...fadeUp} className="mt-10 text-center bg-gradient-to-br from-[#FFF7ED] to-white rounded-2xl p-8 border border-[#F56F05]/10">
            <h3 className="text-lg font-bold text-zinc-900 font-heading mb-2">Still have questions?</h3>
            <p className="text-sm text-zinc-500 mb-5">We're here to help. Reach out and we'll get back to you quickly.</p>
            <Button asChild className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold px-6 h-11 rounded-xl">
              <Link to="/contact">Contact Us <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── Orange CTA ───────────────────────────── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-white font-heading drop-shadow-lg">
              Ready to Experience the Difference?
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Book your professional clean today and discover why Portland homeowners trust Gleam Guru.
            </p>
            <Button asChild className="bg-white text-[#F56F05] hover:bg-zinc-100 font-bold px-10 py-7 rounded-full shadow-xl hover:shadow-2xl transition-all text-lg">
              <Link to="/booking">Book Now <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <a href="tel:9717150335" className="flex items-center justify-center gap-2 text-white/90 hover:text-white transition-colors font-medium">
              <Phone className="h-5 w-5" /> (971) 715-0335
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
